/**
 * WhyBuddyPanel — compact explanation of why Buddy looks the way it does.
 * Shows: Macro, Mempool, Recent event, What it means, Confidence, Trust layer.
 */

import { memo } from 'react';
import { motion } from 'framer-motion';

interface WhyBuddyPanelProps {
  macroState?: string;
  networkState?: string;
  blockState?: string;
  activeSignature?: string | null;
  heatLevel?: number;
  recentEventHeadline?: string | null;
  ltConfidence?: string;
  whatItMeans?: string;
}

// ── Macro label mapping ──
function deriveMacroLabel(macroState?: string): string {
  switch (macroState) {
    case 'DEEP_VALUE': return 'Deep value zone';
    case 'ACCUMULATION': return 'Quiet accumulation';
    case 'NEUTRAL': return 'Wait and watch';
    case 'WARM': return 'Getting crowded';
    case 'HOT': return 'Suspiciously sunny';
    case 'MIXED': return 'Mixed weather';
    default: return 'Observing';
  }
}

// ── Mempool pressure label ──
function deriveMempoolLabel(networkState?: string, heatLevel?: number): string {
  const heat = heatLevel ?? 0;
  if (networkState === 'congested' || heat > 0.65) return 'Busy';
  if (networkState === 'busy' || heat > 0.4) return 'Tense';
  if (networkState === 'easing') return 'Easing';
  return 'Calm';
}

// ── Recent event label ──
function deriveRecentEventLabel(
  blockState?: string,
  activeSignature?: string | null,
  networkState?: string,
  recentEventHeadline?: string | null,
): string {
  if (blockState === 'justFound' || activeSignature === 'reliefExhale') return 'Block found';
  if (activeSignature === 'queueStare') return 'Queue still building';
  if (networkState === 'easing') return 'Pressure easing';
  if (recentEventHeadline) return recentEventHeadline;
  return 'No recent event';
}

// ── What it usually means ──
function deriveWhatItMeans(macroState?: string, networkState?: string): string {
  const macro = macroState ?? 'NEUTRAL';
  const net = networkState ?? 'quiet';
  if (macro === 'DEEP_VALUE') return 'Historically strong accumulation conditions.';
  if (macro === 'ACCUMULATION' && net === 'quiet') return 'Calm conditions — good for patient builders.';
  if (macro === 'ACCUMULATION') return 'Recovering from lows — momentum building slowly.';
  if (macro === 'WARM') return 'Interest rising — conditions are heating up.';
  if (macro === 'HOT' && (net === 'congested' || net === 'busy')) return 'High activity everywhere — caution advised.';
  if (macro === 'HOT') return 'Market running hot — watch for overextension.';
  if (macro === 'MIXED') return 'Signals don\'t agree — wait for clarity.';
  if (net === 'congested') return 'Chain is busy — higher fees expected.';
  return 'Stable conditions — nothing urgent.';
}

// ── Confidence mapping ──
function deriveConfidence(ltConfidence?: string): { level: 'HIGH' | 'MEDIUM' | 'LOW'; label: string } {
  switch (ltConfidence) {
    case 'high': return { level: 'HIGH', label: 'Clear signal' };
    case 'medium': return { level: 'MEDIUM', label: 'Mixed clarity' };
    default: return { level: 'LOW', label: 'Still learning' };
  }
}

// ── Dot colors ──
function macroDotColor(macroState?: string): string {
  switch (macroState) {
    case 'DEEP_VALUE': return 'hsl(var(--energy) / 0.6)';
    case 'ACCUMULATION': return 'hsl(var(--success) / 0.5)';
    case 'WARM': return 'hsl(var(--bitcoin) / 0.6)';
    case 'HOT': return 'hsl(var(--stress) / 0.6)';
    case 'MIXED': return 'hsl(var(--mood) / 0.5)';
    default: return 'hsl(var(--foreground) / 0.25)';
  }
}

function mempoolDotColor(networkState?: string, heatLevel?: number): string {
  const heat = heatLevel ?? 0;
  if (networkState === 'congested' || heat > 0.65) return 'hsl(var(--stress) / 0.5)';
  if (networkState === 'busy' || heat > 0.4) return 'hsl(var(--bitcoin) / 0.5)';
  if (networkState === 'easing') return 'hsl(var(--success) / 0.45)';
  return 'hsl(var(--success) / 0.4)';
}

function eventDotColor(blockState?: string, activeSignature?: string | null): string {
  if (blockState === 'justFound' || activeSignature === 'reliefExhale') return 'hsl(var(--bitcoin) / 0.5)';
  if (activeSignature === 'queueStare') return 'hsl(var(--stress) / 0.4)';
  return 'hsl(var(--foreground) / 0.15)';
}

const CONFIDENCE_LEVELS = ['LOW', 'MEDIUM', 'HIGH'] as const;

function WhyBuddyPanelInner({
  macroState,
  networkState,
  blockState,
  activeSignature,
  heatLevel,
  recentEventHeadline,
  ltConfidence,
  whatItMeans: whatItMeansProp,
}: WhyBuddyPanelProps) {
  const causes = [
    {
      label: 'Macro',
      value: deriveMacroLabel(macroState),
      dot: macroDotColor(macroState),
    },
    {
      label: 'Mempool',
      value: deriveMempoolLabel(networkState, heatLevel),
      dot: mempoolDotColor(networkState, heatLevel),
    },
    {
      label: 'Recent',
      value: deriveRecentEventLabel(blockState, activeSignature, networkState, recentEventHeadline),
      dot: eventDotColor(blockState, activeSignature),
    },
  ];

  const whatItMeans = whatItMeansProp || deriveWhatItMeans(macroState, networkState);
  const confidence = deriveConfidence(ltConfidence);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35, duration: 0.35 }}
      className="w-full max-w-[280px] mx-auto"
    >
      {/* Title */}
      <p
        className="text-[8px] font-body font-semibold tracking-widest uppercase text-center mb-2.5"
        style={{ color: 'hsl(var(--foreground) / 0.22)' }}
      >
        Why Buddy looks like this
      </p>

      {/* 3 cause rows */}
      <div
        className="rounded-xl px-3.5 py-2.5 space-y-2"
        style={{
          background: 'hsl(var(--foreground) / 0.04)',
          border: '1px solid hsl(var(--foreground) / 0.06)',
        }}
      >
        {causes.map((cause) => (
          <div key={cause.label} className="flex items-center gap-2.5">
            <div
              className="w-[5px] h-[5px] rounded-full shrink-0"
              style={{ background: cause.dot }}
            />
            <span
              className="text-[8px] font-body font-medium uppercase tracking-wider shrink-0"
              style={{ color: 'hsl(var(--foreground) / 0.3)', minWidth: 48 }}
            >
              {cause.label}
            </span>
            <span
              className="text-[10px] font-body font-medium truncate"
              style={{ color: 'hsl(var(--foreground) / 0.55)' }}
            >
              {cause.value}
            </span>
          </div>
        ))}
      </div>

      {/* What it usually means */}
      <div className="mt-3 px-1">
        <p
          className="text-[7px] font-body font-semibold uppercase tracking-widest mb-1"
          style={{ color: 'hsl(var(--foreground) / 0.18)' }}
        >
          What it usually means
        </p>
        <p
          className="text-[10px] font-body font-light leading-[1.55] italic"
          style={{ color: 'hsl(var(--foreground) / 0.42)' }}
        >
          {whatItMeans}
        </p>
      </div>

      {/* Confidence */}
      <div className="mt-3 flex items-center justify-center gap-1.5">
        {CONFIDENCE_LEVELS.map((level) => (
          <div
            key={level}
            className="w-1.5 h-1.5 rounded-full transition-colors"
            style={{
              background: CONFIDENCE_LEVELS.indexOf(confidence.level) >= CONFIDENCE_LEVELS.indexOf(level)
                ? 'hsl(var(--foreground) / 0.35)'
                : 'hsl(var(--foreground) / 0.08)',
            }}
          />
        ))}
        <span
          className="text-[7px] font-body font-medium uppercase tracking-wider ml-1"
          style={{ color: 'hsl(var(--foreground) / 0.22)' }}
        >
          {confidence.label}
        </span>
      </div>

      {/* Divider */}
      <div
        className="mx-auto mt-3 mb-2.5"
        style={{
          height: 1,
          width: '60%',
          background: 'linear-gradient(90deg, transparent, hsl(var(--foreground) / 0.06), transparent)',
        }}
      />

      {/* Trust layer — What Buddy watches */}
      <div className="px-1">
        <p
          className="text-[7px] font-body font-semibold uppercase tracking-widest mb-1.5 text-center"
          style={{ color: 'hsl(var(--foreground) / 0.16)' }}
        >
          What Buddy watches
        </p>
        <div className="flex items-center justify-center gap-3">
          {[
            { emoji: '📈', label: 'Long-term trend' },
            { emoji: '🔗', label: 'Chain pressure' },
            { emoji: '📦', label: 'Block activity' },
          ].map((input) => (
            <div key={input.label} className="flex items-center gap-1">
              <span className="text-[8px]">{input.emoji}</span>
              <span
                className="text-[8px] font-body font-light"
                style={{ color: 'hsl(var(--foreground) / 0.3)' }}
              >
                {input.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Helper line */}
      <p
        className="text-[7px] font-body font-light text-center mt-2.5 italic"
        style={{ color: 'hsl(var(--foreground) / 0.14)' }}
      >
        This read is based on market conditions plus what the chain is doing right now.
      </p>
    </motion.div>
  );
}

const WhyBuddyPanel = memo(WhyBuddyPanelInner);
export default WhyBuddyPanel;
